import React, { useState, useEffect } from "react";
import { callLogTemplates } from "../popData";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import PhoneIcon from "@mui/icons-material/Phone";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import { dateTimeFormatter } from "../helperFunctions/dateFormatters";
import MailIcon from "@mui/icons-material/Mail";
import { doc } from "firebase/firestore";
import { updatePrimary } from "../helperFunctions/primaryWrites";

const callIcons = {
  phone: <PhoneIcon className="icon" />,
  mail: <MailIcon className="icon" />,
  booked: <EventAvailableIcon className="icon" />,
};

// Matches a "[label](url)" link first (what automated notes now write - see
// buildSentLogEntry in pendingDraftsCheck.js - so the link shows a short word like "Gmail"
// instead of the raw thread URL), falling back to linkifying a bare URL as-is for older
// already-logged entries that still have one written straight into the text. Built fresh
// per call (rather than a shared module-level /g regex) so repeated calls never trip over
// leftover .lastIndex state from a previous match.
function linkifyText(text) {
  if (!text) return text;

  let linkRegex = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)|(https?:\/\/[^\s]+)/g;
  let parts = [];
  let lastIndex = 0;
  let match;
  let key = 0;

  while ((match = linkRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }

    let [full, label, labeledUrl, bareUrl] = match;
    let url = labeledUrl || bareUrl;
    let matchEnd = match.index + full.length;

    // A bare URL (the legacy, unlabeled form) commonly sits at the end of a sentence - trim
    // trailing punctuation so it isn't folded into the link's href/text. The labeled
    // "[x](url)" form doesn't need this: its url is already delimited by the closing ")".
    if (bareUrl) {
      let trimmed = url.replace(/[.,;:!?)]+$/, "");
      matchEnd -= url.length - trimmed.length;
      url = trimmed;
    }

    parts.push(
      <a key={key++} href={url} target="_blank" rel="noopener noreferrer">
        {label || url}
      </a>
    );

    lastIndex = matchEnd;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts;
}

const ContactLog = (props) => {
  let { formData, dbRef, params } = props;
  const [orderedCallLogs, setOrderedCallLogs] = useState(orderCallLogs());

  const [callAdderForm, setCallAdderForm] = useState({
    icon: "phone",
    note: "",
    time: dateTimeFormatter(new Date(), "t_short"),
    date: dateTimeFormatter(new Date(), "d_full_variant"),
  });

  useEffect(() => {
    setOrderedCallLogs(orderCallLogs());
  }, [formData.callLog]);

  async function updateDbCallLog(updatedLog) {
    updatePrimary(doc(dbRef, `${params?.uid}`), { callLog: updatedLog });
  }

  async function addCallLog(callAdderForm) {
    let currentLog = formData?.callLog || [];
    let updatedLog = [...currentLog];
    updatedLog.push(callAdderForm);

    updateDbCallLog(updatedLog);

    // setOrderedCallLogs(orderCallLogs());
  }

  function removeCallLog(callLogIndex) {
    let updatedLog = orderedCallLogs.filter((log, i) => {
      return i !== callLogIndex;
    });

    updateDbCallLog(updatedLog);
  }

  function handleCallAdderInput(e) {
    let name = e.target.name;
    let value = e.target.value;

    setCallAdderForm({ ...callAdderForm, [name]: value });
  }

  function orderCallLogs() {
    let tempData = formData ? { ...formData } : {};
    let callLog = tempData?.callLog || [];
    return callLog?.sort(
      (a, b) =>
        new Date(b?.date).getTime() -
        new Date(a?.date).getTime() +
        b.time.localeCompare(a.time)
      // (a, b) => new Date(b?.date).getTime() - new Date(a?.date).getTime()
    );
  }

  function addLogTempate(text) {
    let noteWithTemplate = callAdderForm.note + text;
    setCallAdderForm({ ...callAdderForm, note: noteWithTemplate });
  }

  function cycleCallIcons() {
    if (callAdderForm.icon === "phone") {
      setCallAdderForm({ ...callAdderForm, icon: "mail" });
    } else if (callAdderForm.icon === "mail") {
      setCallAdderForm({ ...callAdderForm, icon: "booked" });
    } else {
      setCallAdderForm({ ...callAdderForm, icon: "phone" });
    }
  }

  // console.log("c", orderedCallLogs);
  return (
    <div className="section">
      <h3 className="heading">Contact Log</h3>
      <div className="callLog">
        {/* <button onClick={() => toggleCalls((pS) => !pS)}>Toggle</button> */}

        <div className="logTemplates">
          {callLogTemplates[callAdderForm.icon].templates.map((template, i) => {
            return (
              <span key={i} onClick={() => addLogTempate(template.text)}>
                {template.name}
              </span>
            );
          })}
          <span
            className="clear"
            onClick={() => {
              setCallAdderForm({ ...callAdderForm, note: "" });
            }}
          >
            Clear
          </span>
        </div>
        <div className="callsAdder">
          <div className="callControls">
            <button onClick={cycleCallIcons} className="callType">
              {/* <PhoneIcon className="callIcon" /> */}
              {callIcons[callAdderForm?.icon]}
              {/* <MailIcon className="callIcon" /> */}
            </button>
            <input
              onChange={handleCallAdderInput}
              value={callAdderForm?.time ?? ""}
              name="time"
              id="time"
              className="callTime"
              type="time"
            />
            <input
              onChange={handleCallAdderInput}
              value={callAdderForm?.date}
              name="date"
              id="date"
              className="callDate"
              type="date"
            />
          </div>

          <textarea
            onChange={handleCallAdderInput}
            name="note"
            value={callAdderForm?.note ?? ""}
            id="note"
            className="callNote"
            rows={3}
          />

          <button
            onClick={() => addCallLog(callAdderForm)}
            className="callEnter"
          >
            Add
          </button>
        </div>
        <div className="callList">
          {orderedCallLogs?.map((cl, i) => {
            return (
              <div className="callLine" key={i}>
                <div className="callData">
                  <p className="callIcon">{callIcons[cl.icon]}</p>
                  <p className="callTime">
                    {dateTimeFormatter(cl.date, "d_medium")}
                    {cl?.time && cl?.date ? ` - ` : ""}
                    {cl?.time ? <span>{cl.time}</span> : ""}
                  </p>
                </div>
                <p className="callNote">{linkifyText(cl.note)}</p>
                <button
                  onClick={() => removeCallLog(i)}
                  className="removeCallLogBtn"
                >
                  <HighlightOffIcon />
                </button>
              </div>

              // Add date!! and delete button
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default React.memo(ContactLog);
